import React, { useMemo, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { usePatternSidePanelContext } from '../../contexts/PatternSidePanelContext'
import { PatternTreeContext } from '../../contexts/PatternTreeContext'
// Direct import (not the copilot barrel) keeps react-markdown out of this
// module graph; the context has a safe default outside the provider.
import { useCopilotStatus } from '../../copilot/contexts/PatternCopilotContext'
import { collection, doc, orderBy, query, updateDoc } from 'firebase/firestore'
import { firebase_firestore } from '../../../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../../../contexts/ApplicationSuiteProvider'
import * as jsondiffpatch from 'jsondiffpatch'
import { diff_match_patch } from 'diff-match-patch'
import ReactGA from 'react-ga4'
import { useCollectionData, useDocument } from 'react-firebase-hooks/firestore'

// Define the version type
interface VersionType {
  version: number
  created: number
  diff: string
  starred?: boolean
  note?: string
  hidden?: boolean
  source?: string
  agentSessionId?: string
}

const VersionTab: React.FC = () => {
  const { selectedPattern } = usePatternSidePanelContext()
  const { setPattern } = React.useContext(PatternTreeContext)
  const { status: copilotStatus } = useCopilotStatus()
  const { usermeta, tradingHouse } = useApplicationSuiteContext()
  const [error, setError] = useState<string | null>(null)
  const [showOnlyStarred, setShowOnlyStarred] = useState(false)
  const [showHidden, setShowHidden] = useState(false)
  const [editingNoteVersion, setEditingNoteVersion] = useState<number | null>(null)
  const [noteText, setNoteText] = useState('')

  // Create the jsondiffpatch instance
  const jsondiffpatchInstance = jsondiffpatch.create({
    textDiff: {
      diffMatchPatch: diff_match_patch,
    },
  })

  // Check if pattern exists in user's patterns
  const userDocPath = useMemo(() => {
    if (!selectedPattern?.id || !usermeta?.gcid) return null
    return `usermeta/${usermeta.gcid}/patterns/${selectedPattern.id}`
  }, [selectedPattern?.id, usermeta?.gcid])

  const [userPatternDoc, userPatternLoading] = useDocument(
    userDocPath ? doc(firebase_firestore, userDocPath) : null
  )

  // Determine the correct collection path based on whether user owns the pattern
  const versionsCollectionPath = useMemo(() => {
    if (!selectedPattern?.id) return null

    if (userPatternDoc?.exists()) {
      return `${userDocPath}/versions`
    } else if (tradingHouse) {
      return `tradingHouses/${tradingHouse}/standardPatterns/${selectedPattern.id}/versions`
    }

    return null
  }, [selectedPattern?.id, userDocPath, userPatternDoc, tradingHouse])

  // Query the versions using the hook
  const [versionsData, versionsLoading] = useCollectionData(
    versionsCollectionPath
      ? query(collection(firebase_firestore, versionsCollectionPath), orderBy('version'))
      : null
  )

  // Convert the raw data to our version type
  const versions = useMemo(() => {
    return (versionsData as VersionType[]) || []
  }, [versionsData])

  // Get the latest version number from the versions data
  const latestVersionNumber = useMemo(() => {
    if (!versions.length) return 0
    return Math.max(...versions.map((v) => v.version))
  }, [versions])

  // Track the previous versions array to detect when new versions are added
  const [previousVersionCount, setPreviousVersionCount] = useState(0)

  // Update the version count when it changes
  useEffect(() => {
    if (versions.length !== previousVersionCount) {
      setPreviousVersionCount(versions.length)
    }
  }, [versions.length, previousVersionCount])

  // Sync selectedPattern with the latest version if needed
  useEffect(() => {
    if (!selectedPattern || !versions.length) return
    // Never clobber the editor while the AI copilot is mid-turn (its applies
    // are unsaved tree state; this auto-apply would overwrite them)
    if (copilotStatus !== 'idle') return

    // Case 1: Pattern doesn't have a version yet, set it to the latest
    // Case 2: A new version was just created (version count increased) - likely from user edit
    const versionJustCreated = versions.length > previousVersionCount && previousVersionCount > 0

    if (latestVersionNumber > 0 && (!selectedPattern.version || versionJustCreated)) {
      // Fetch latest version data to update the pattern
      const applyLatestVersion = () => {
        try {
          // Start with the first version as base
          let obj = jsondiffpatchInstance.patch({}, JSON.parse(versions[0].diff))

          // Apply each diff sequentially until we reach the latest version
          for (let i = 1; i < latestVersionNumber; i++) {
            const versionToApply = versions.find((v) => v.version === i + 1)
            if (versionToApply) {
              obj = jsondiffpatchInstance.patch(obj, JSON.parse(versionToApply.diff))
            }
          }

          // Update the editor pattern state to reflect latest version
          const latestPattern = {
            ...obj,
            id: selectedPattern.id,
            version: latestVersionNumber,
          }
          setPattern(latestPattern)

          // Log analytics event
          ReactGA.event('pattern_auto_updated_to_latest_version', {
            version: latestVersionNumber,
            reason: versionJustCreated ? 'new_version_created' : 'initial_load',
          })
        } catch (err) {
          console.error('Error auto-applying latest version:', err)
          setError('Failed to sync with latest version')
        }
      }

      applyLatestVersion()
    }
  }, [
    latestVersionNumber,
    selectedPattern,
    versions,
    versions.length,
    previousVersionCount,
    jsondiffpatchInstance,
    setPattern,
    copilotStatus,
  ])

  // Apply a specific version to the pattern
  const applyVersion = (targetVersion: number) => {
    if (!versions.length || !selectedPattern) return

    try {
      // Start with the first version as base
      let obj = jsondiffpatchInstance.patch({}, JSON.parse(versions[0].diff))

      // Apply each diff sequentially until we reach the desired version
      for (let i = 1; i < targetVersion; i++) {
        const versionToApply = versions.find((v) => v.version === i + 1)
        if (versionToApply) {
          obj = jsondiffpatchInstance.patch(obj, JSON.parse(versionToApply.diff))
        }
      }

      // Log analytics event
      ReactGA.event('pattern_applied_version', {
        version: targetVersion,
      })

      const versionedPattern = {
        ...obj,
        id: selectedPattern.id,
        version: targetVersion,
      }

      // Update the editor pattern state (syncs to side panel via PatternDetailViewInner)
      setPattern(versionedPattern)
    } catch (err) {
      console.error('Error applying version:', err)
      setError('Failed to apply version')
    }
  }

  // Format the date for display
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }

  const toggleStar = async (version: VersionType) => {
    if (!versionsCollectionPath) return
    try {
      const versionRef = doc(firebase_firestore, `${versionsCollectionPath}/${version.version}`)
      await updateDoc(versionRef, { starred: !version.starred })
    } catch (err) {
      console.error('Error toggling star:', err)
      setError('Failed to update version')
    }
  }

  const saveNote = async (versionNumber: number) => {
    if (!versionsCollectionPath) return
    try {
      const versionRef = doc(firebase_firestore, `${versionsCollectionPath}/${versionNumber}`)
      await updateDoc(versionRef, { note: noteText })
      setEditingNoteVersion(null)
    } catch (err) {
      console.error('Error saving note:', err)
      setError('Failed to save note')
    }
  }

  const toggleHide = async (version: VersionType) => {
    if (!versionsCollectionPath) return
    try {
      const versionRef = doc(firebase_firestore, `${versionsCollectionPath}/${version.version}`)
      await updateDoc(versionRef, { hidden: !version.hidden })
    } catch (err) {
      console.error('Error toggling hide:', err)
      setError('Failed to update version')
    }
  }

  // Show loading state
  if (versionsLoading || userPatternLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  if (!selectedPattern) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No pattern selected</Typography>
      </Box>
    )
  }

  // Get the current viewing version from the pattern
  const currentViewingVersion = selectedPattern.version || 1

  // Sort versions to have most recent at top
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version)

  // Count hidden versions
  const hiddenCount = versions.filter((v) => v.hidden).length

  // Apply filters
  const filteredVersions = sortedVersions.filter((v) => {
    if (showOnlyStarred && !v.starred) return false
    if (!showHidden && v.hidden) return false
    return true
  })

  return (
    <Box sx={{ p: 1 }}>
      {/* Filter toolbar */}
      {sortedVersions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<StarIcon fontSize="small" />}
            label="Favorites"
            size="small"
            variant={showOnlyStarred ? 'filled' : 'outlined'}
            color={showOnlyStarred ? 'warning' : 'default'}
            onClick={() => setShowOnlyStarred(!showOnlyStarred)}
          />
          {hiddenCount > 0 && (
            <Chip
              icon={<VisibilityOffIcon fontSize="small" />}
              label={`Hidden (${hiddenCount})`}
              size="small"
              variant={showHidden ? 'filled' : 'outlined'}
              color={showHidden ? 'default' : 'default'}
              onClick={() => setShowHidden(!showHidden)}
            />
          )}
        </Box>
      )}

      {filteredVersions.length > 0 ? (
        <List sx={{ width: '100%' }}>
          {filteredVersions.map((version, index) => {
            const isViewing = version.version === currentViewingVersion
            const canHide = version.version !== 1 && !isViewing

            return (
              <React.Fragment key={version.version}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  sx={{
                    py: 1.5,
                    opacity: version.hidden ? 0.5 : 1,
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip
                        title={
                          version.starred
                            ? `Unfavorite Version ${version.version}`
                            : `Favorite Version ${version.version}`
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleStar(version)
                          }}
                        >
                          {version.starred ? (
                            <StarIcon fontSize="small" color="warning" />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {canHide && (
                        <Tooltip title={version.hidden ? 'Unhide' : 'Hide'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleHide(version)
                            }}
                          >
                            {version.hidden ? (
                              <VisibilityIcon fontSize="small" />
                            ) : (
                              <VisibilityOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {isViewing ? (
                        <Chip label="Viewing" size="small" color="success" sx={{ height: 24 }} />
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => applyVersion(version.version)}
                        >
                          View
                        </Button>
                      )}
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                          variant="body1"
                          color="text.primary"
                          sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}
                        >
                          Version {version.version}
                        </Typography>
                        {version.source === 'strategy-agent' && (
                          <Tooltip title="Created by AI Session">
                            <SmartToyOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontFamily: "'JetBrains Mono', monospace",
                            opacity: 0.6,
                            fontSize: '0.75rem',
                          }}
                        >
                          {formatDate(version.created)}
                        </Typography>
                        {editingNoteVersion === version.version ? (
                          <Box
                            sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TextField
                              size="small"
                              variant="outlined"
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="Add a note..."
                              inputProps={{ maxLength: 200 }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveNote(version.version)
                                if (e.key === 'Escape') setEditingNoteVersion(null)
                              }}
                              autoFocus
                              fullWidth
                              sx={{ maxWidth: 200 }}
                            />
                            <IconButton size="small" onClick={() => saveNote(version.version)}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setEditingNoteVersion(null)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : version.note ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              pl: 1,
                              borderLeft: '2px solid rgba(29, 195, 14, 0.3)',
                              fontStyle: 'italic',
                              cursor: 'pointer',
                              '&:hover': { color: 'text.primary' },
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              setNoteText(version.note || '')
                              setEditingNoteVersion(version.version)
                            }}
                          >
                            {version.note}
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              cursor: 'pointer',
                              '&:hover': { color: 'text.secondary' },
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              setNoteText('')
                              setEditingNoteVersion(version.version)
                            }}
                          >
                            Add note...
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              </React.Fragment>
            )
          })}
        </List>
      ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {showOnlyStarred ? 'No favorite versions' : 'No version history available'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default VersionTab
